import React, { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import SubmitButton from '../../components/Common/SubmitButton';
import CheckoutForm from '../../components/Stripe/CheckoutForm';
import Heading from '../Common/Heading';
import Loader from '../Common/Loader';
import { init, trackPageView } from 'goaffpro-client';
import ThankYou from '../Common/ThankYou';
import Guide from '../Common/Guide';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { applyPromoCodeService, handlePaymentService } from '../Common/services/LowCreditModal.service';
import { StoryPurchaseTypeEnum } from '../../interfaces/book.interface';

init(process.env.REACT_APP_GOAFFPRO_PUBLIC_KEY!);

interface LowCreditModalProps {
  showLowCreditModal?: boolean;
  setShowLowCreditModal?: (value: boolean) => void;
}

const LowCreditModal = ({ showLowCreditModal = true, setShowLowCreditModal }: LowCreditModalProps) => {
  const [showThanksNote, setShowThanksNote] = useState<boolean>(false);
  const [couponLoader, setCouponLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const bookData = useSelector((state: RootState) => state.app.bookData);
  const [paymentLoader, setPaymentLoader] = useState<boolean>(false);
  const [cardHolderName, setCardHolderName] = useState<string>('');
  const [coupon, setCoupon] = useState<string | null>(null);
  const [couponClass, setCouponClass] = useState<string | null>(null);
  const [discount, setDiscount] = useState<string | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [freeOfCost, setFreeOfCost] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    trackPageView();
  }, []);

  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState(
    bookData?.bookId ? StoryPurchaseTypeEnum.CREDITS : StoryPurchaseTypeEnum.STORY
  );

  const [couponDescription, setCouponDescription] = useState<string | null>(null);

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedPurchaseOption(event.target.value as StoryPurchaseTypeEnum);
  };

  useEffect(() => {
    if (selectedPurchaseOption === StoryPurchaseTypeEnum.CREDITS) setPrice(user?.plan?.additionalAICredit?.price ?? null);
    else if (selectedPurchaseOption === StoryPurchaseTypeEnum.STORY)
      setPrice(user?.plan?.additionalAICredit?.storyCreationCost ?? null);
  }, [selectedPurchaseOption]);

  useEffect(() => {
    //If coupon applied having more discount then current charging amount, price goes to -ve
    if (parseFloat(`${price}`) < 0) setPrice(0);
    else if (price === 0) setFreeOfCost(true);
  }, [price]);

  useEffect(() => {
    if (selectedPurchaseOption === StoryPurchaseTypeEnum.CREDITS) {
      if (discount && parseFloat(discount) >= parseFloat(user?.plan?.additionalAICredit?.price?.toString() ?? '0')) {
        setFreeOfCost(true);
      }
    } else if (selectedPurchaseOption === StoryPurchaseTypeEnum.STORY) {
      if (discount && parseFloat(discount) >= parseFloat(user?.plan?.additionalAICredit?.storyCreationCost?.toString() ?? '0')) {
        setFreeOfCost(true);
      }
    }
  }, [discount]);

  const handlePayment = async () => {
    await handlePaymentService(
      stripe,
      elements,
      user,
      selectedPurchaseOption,
      freeOfCost,
      coupon,
      bookData,
      setShowThanksNote,
      setShowLowCreditModal,
      setPaymentLoader
    );
  };

  const handleApplyPromoCode = async (couponCode: string | null) => {
    await applyPromoCodeService(
      couponCode,
      user,
      selectedPurchaseOption,
      setCouponClass,
      setCouponDescription,
      setPrice,
      setDiscount,
      setCouponLoader
    );
  };

  const goBack = () => {
    navigate(-1);
    setShowLowCreditModal && setShowLowCreditModal(false);
    setShowThanksNote(false);
  };

  return (
    <>
      {showThanksNote ? (
        <ThankYou goBack={goBack} />
      ) : (
        <div aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className=" flex items-center align-end mb-4">
            <Heading title="Buy Illustration & Story Credits" />
            <div>
              <Guide />
            </div>
          </div>
          <div className="flex gap-4  items-center">
            {bookData?.bookId && (
              <label className="inline-flex items-center font-bold text-storygreen">
                <input
                  type="radio"
                  className="form-radio text-storygreen"
                  name="credits"
                  value={StoryPurchaseTypeEnum.CREDITS}
                  checked={selectedPurchaseOption === StoryPurchaseTypeEnum.CREDITS}
                  onChange={handleOptionChange}
                />
                <span className="ml-2">Buy Illustrations</span>
              </label>
            )}

            <label className="inline-flex items-center font-bold text-storygreen">
              <input
                type="radio"
                className="form-radio text-storyblue"
                name="story"
                value={StoryPurchaseTypeEnum.STORY}
                checked={selectedPurchaseOption === StoryPurchaseTypeEnum.STORY}
                onChange={handleOptionChange}
              />
              <span className="ml-2">Buy Story</span>
            </label>
          </div>
          <div className="modal-content">
            <div className="bg-storytan p-0 mx-auto">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handlePayment();
                }}
              >
                <div className="max-w-7xl mx-auto">
                  {selectedPurchaseOption === StoryPurchaseTypeEnum.CREDITS ? (
                    <div className="w-full flex justify-between ">
                      <h1 className=" flex items-start font-bold text-storyblue text-md mt-6 ">
                        Current Image Credits are:
                        <span
                          className="ml-2 font-normal">{bookData?.creditsPerBookLimit || 0}</span>
                      </h1>
                      <h1 className=" flex items-start font-bold text-storyblue text-md mt-6 ">
                        {bookData?.title ? (
                          <span className="ml-2 font-normal">
                            <strong>Book:</strong> {bookData?.title}
                          </span>
                        ) : (
                          ''
                        )}
                      </h1>
                    </div>
                  ) : selectedPurchaseOption === StoryPurchaseTypeEnum.STORY ? (
                    <h1 className=" flex items-start font-bold text-storyblue text-md mt-6 ">
                      Current Story Creation Limit is:
                      <span className="ml-2 font-normal">
                        {+(user?.booksCreateLimit ?? 0) - +(user?.booksCreatedCounter ?? 0)}
                      </span>
                    </h1>
                  ) : (
                    ''
                  )}
                  <div
                    className="bg-white rounded-md flex flex-col gap-3 justify-around items-start w-[100%] my-2 p-2">
                    <h1 className="text-md text-storyblue">
                      <strong>Your Current Plan:</strong> <span>{user?.plan?.name}</span>
                    </h1>
                    {selectedPurchaseOption === StoryPurchaseTypeEnum.CREDITS ? (
                      <>
                        {' '}
                        <h1 className="text-md text-left text-storyblue">
                          <strong>Additional Image Credits Per Book: </strong>
                          {user?.plan?.additionalAICredit?.credits} Image Credits for ${' '}
                          {user?.plan?.additionalAICredit?.price}
                        </h1>
                      </>
                    ) : (
                      <>
                        {' '}
                        <h1 className="text-md text-left text-storyblue">
                          <strong>Additional Story Creation Limit: </strong>
                          {user?.plan?.additionalAICredit?.storyCreationLimit} Stories for
                          ${' '}
                          {user?.plan?.additionalAICredit?.storyCreationCost}
                        </h1>
                      </>
                    )}
                  </div>
                  <div className="w-full flex flex-col gap-2">
                    <div className="mt-5  rounded-md  h-10 ">
                      <div className="flex items-center mt-2 gap-2 rounded-md">
                        <input
                          type="text"
                          name="coupon"
                          value={coupon ?? ''}
                          onChange={(e) => {
                            setCoupon(e.target.value);
                          }}
                          placeholder="Coupon (Optional)"
                          className={`w-full md:w-[20%] p-2 rounded-md uppercase ${couponClass} `}
                        />
                        <button
                          type="button"
                          className={`${
                            !coupon ? 'pointer-events-none opacity-70' : ''
                          } w-72 flex justify-center items-center gap-1 bg-storyblueSecondLightText text-white whitespace-nowrap font-bold px-2 rounded-md text-xs md:text-sm h-9`}
                          onClick={() => handleApplyPromoCode(coupon)}
                          disabled={!coupon}
                        >
                          Apply Coupon / Gift Card {couponLoader &&
                          <Loader loader={couponLoader} />}
                        </button>
                      </div>
                    </div>
                    {freeOfCost === false && (
                      <>
                        <div className="mt-5  rounded-md h-10 ">
                          <input
                            type="text"
                            required
                            name="billingCardHolderName"
                            value={cardHolderName}
                            onChange={(e) => {
                              setCardHolderName(e.target.value);
                            }}
                            placeholder="Card Holder Name"
                            className="p-2 w-full rounded-md"
                          />
                        </div>

                        <div className="w-full bg-white p-2 rounded-md h-10">
                          <CheckoutForm />
                        </div>
                      </>
                    )}

                    <p className="flex pt-2 text-storygreen text-sm">
                      {' '}
                      {discount && (
                        <span className=" text-storygreen text-sm">
                          Discount applied of{' '}
                          <b className="pl-1">
                            {' '}
                            ${' '}
                            {parseFloat(discount) > parseFloat(user?.plan?.additionalAICredit?.price?.toString() ?? '0')
                              ? user?.plan?.additionalAICredit?.price
                              : discount}
                          </b>{' '}
                        </span>
                      )}{' '}
                      You will be charged <b className="pl-1"> $ {price}</b>
                    </p>
                    {discount && couponDescription && couponDescription.length > 0 && (
                      <div className="flex pt-0">
                        <span className=" text-storygreen text-sm">
                          <strong> Description: </strong> {couponDescription}
                        </span>
                      </div>
                    )}
                    <SubmitButton title="Buy More Credits" loader={paymentLoader}
                                  cancelOperation={goBack} />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default LowCreditModal;
